import React, { useState } from 'react';
import { MessageSquare, Bug, Lightbulb, ExternalLink, Clock, Users, Shield } from 'lucide-react';

export const ContactPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string>('general');

  const contactTypes = [
    {
      id: 'general',
      title: 'General Inquiry',
      description: 'Questions about our tools or services',
      icon: <MessageSquare className="h-6 w-6" />,
      formUrl: 'https://forms.gle/1VeG6s9vTh1r51rPA' // Replace with your actual Google Form URL
    },
    {
      id: 'bug',
      title: 'Bug Report',
      description: 'Report issues or problems with our tools',
      icon: <Bug className="h-6 w-6" />,
      formUrl: 'https://forms.gle/rizaskFeMDnzLYNm6' // Replace with your actual Google Form URL
    },
    {
      id: 'feature',
      title: 'Feature Request',
      description: 'Suggest new tools or improvements',
      icon: <Lightbulb className="h-6 w-6" />,
      formUrl: 'https://forms.gle/LBL8WMmLspti3YUv6' // Replace with your actual Google Form URL
    }
  ];

  const selectedContact = contactTypes.find(type => type.id === selectedType);

  const handleOpenForm = () => {
    if (selectedContact) {
      window.open(selectedContact.formUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            We'd love to hear from you! Whether you have questions, feedback, or need support, 
            we're here to help. Choose the type of inquiry below to get started.
          </p>
        </div>

        {/* Contact Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {contactTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`p-6 rounded-lg border-2 transition-all text-left ${
                selectedType === type.id
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className={`mb-3 ${
                selectedType === type.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
              }`}>
                {type.icon}
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${
                selectedType === type.id ? 'text-blue-900 dark:text-blue-400' : 'text-gray-900 dark:text-white'
              }`}>
                {type.title}
              </h3>
              <p className={`text-sm ${
                selectedType === type.id ? 'text-blue-700 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
              }`}>
                {type.description}
              </p>
            </button>
          ))}
        </div>

        {/* Selected Contact Form */}
        {selectedContact && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="text-blue-600 dark:text-blue-400">
                {selectedContact.icon}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedContact.title}
              </h2>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-3">
                Before you submit:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-blue-700 dark:text-blue-400 text-sm">
                {selectedType === 'general' && (
                  <>
                    <li>Check our <a href="/faq" className="underline hover:text-blue-800 dark:hover:text-blue-300">FAQ page</a> for common questions</li>
                    <li>Review our <a href="/how-to-use" className="underline hover:text-blue-800 dark:hover:text-blue-300">How to Use guide</a> for tool instructions</li>
                    <li>Include specific details about your question or concern</li>
                  </>
                )}
                {selectedType === 'bug' && (
                  <>
                    <li>Include your browser version and operating system</li>
                    <li>Describe the steps to reproduce the issue</li>
                    <li>Mention the specific tool where the problem occurred</li>
                    <li>Include any error messages you received</li>
                  </>
                )}
                {selectedType === 'feature' && (
                  <>
                    <li>Describe the feature or improvement in detail</li>
                    <li>Explain how it would benefit users</li>
                    <li>Consider if similar functionality already exists</li>
                    <li>Think about privacy implications of your request</li>
                  </>
                )}
              </ul>
            </div>

            <div className="text-center">
              <button
                onClick={handleOpenForm}
                className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-lg"
              >
                <ExternalLink className="h-5 w-5 mr-2" />
                Open {selectedContact.title} Form
              </button>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                This will open a secure Google Form in a new tab
              </p>
            </div>
          </div>
        )}

        {/* Response Time */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Response Time
            </h3>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            We typically respond within 24-48 hours during business days. 
            Complex technical issues may take longer to investigate.
          </p>
        </div>

        {/* Why Contact Us */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Why Contact Us?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">User-Driven Development</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your feedback directly influences our roadmap and helps us build tools you actually need.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Privacy First</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We're committed to maintaining complete privacy in all our tools and communications.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Quick Support</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Get help with any issues or questions about using our tools effectively.
              </p>
            </div>
          </div>
        </div>

        {/* Common Issues */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <h3 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-4">
            Quick Solutions for Common Issues:
          </h3>
          <div className="space-y-3 text-sm text-yellow-700 dark:text-yellow-400">
            <div>
              <strong>Tool not loading:</strong> Try refreshing the page or clearing your browser cache
            </div>
            <div>
              <strong>File processing fails:</strong> Check file size limits and ensure the file isn't corrupted
            </div>
            <div>
              <strong>Slow performance:</strong> Large files may take time; try processing smaller files or batches
            </div>
            <div>
              <strong>Download issues:</strong> Disable popup blockers and check your download folder
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-green-800 dark:text-green-400 text-sm text-center">
            🔒 Your contact information is used solely for responding to your inquiry. We never share your data with third parties and maintain the same privacy standards as our tools.
          </p>
        </div>
      </div>
    </div>
  );
};